import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

// ---------------------------------------------------------------------------
// Config variables: the keys an analytic reads and each municipality's value.
// ---------------------------------------------------------------------------

const configKeysUrl = (analyticId) => `admin/analytics_data/${analyticId}/config_keys`
const configKeysQueryKey = (analyticId) => ['admin-analytic-config-keys', analyticId]

export function useAdminAnalyticConfigKeys({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => apiGet({ url: configKeysUrl(analyticId), token })
  return useQuery(configKeysQueryKey(analyticId), queryFn, { enabled: !!analyticId, ...options })
}

// Every config mutation changes what the config tab shows, and adding or
// removing a key also changes the analytic's own `config_keys`.
const useConfigMutation = (analyticId, mutationFn, options) => {
  const queryClient = useQueryClient()

  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(configKeysQueryKey(analyticId))
      queryClient.invalidateQueries(['admin-analytic-data', analyticId])
      if (options?.onSuccess) {
        options.onSuccess(data, variables)
      }
    }
  })
}

export function useCreateAdminAnalyticConfigKey({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  return useConfigMutation(
    analyticId,
    (params) => apiPost({ url: configKeysUrl(analyticId), token, objectName: 'config_key', params }),
    options
  )
}

// Edits the key's shared definition, so every analytic reading it changes.
export function useUpdateAdminAnalyticConfigKey({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  return useConfigMutation(
    analyticId,
    ({ keyId, params }) =>
      apiPatch({ url: `${configKeysUrl(analyticId)}/${keyId}`, token, objectName: 'config_key', params }),
    options
  )
}

export function useDeleteAdminAnalyticConfigKey({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  return useConfigMutation(
    analyticId,
    (keyId) => apiDelete({ url: `${configKeysUrl(analyticId)}/${keyId}`, token }),
    options
  )
}

export function useApplyAdminAnalyticConfigDefaults({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  return useConfigMutation(
    analyticId,
    (keyId) => apiPost({ url: `${configKeysUrl(analyticId)}/${keyId}/apply_defaults`, token }),
    options
  )
}

// Syncs every analytic's config keys to what the deployed analytics code reads,
// so every analytic's cached config is stale afterwards.
export function useSyncAdminConfigKeysFromCode({ options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(() => apiPost({ url: 'admin/config_key_sync', token }), {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries('admin-analytic-config-keys')
      queryClient.invalidateQueries('admin-analytic-data')
      queryClient.invalidateQueries('admin-config-key-sync-runs')
      if (options?.onSuccess) {
        options.onSuccess(data, variables)
      }
    }
  })
}

export function useAdminConfigKeySyncRuns({ options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => apiGet({ url: 'admin/config_key_sync_runs', token })
  return useQuery('admin-config-key-sync-runs', queryFn, options)
}

export function useAdminConfigKeySyncRun({ runId, options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => apiGet({ url: `admin/config_key_sync_runs/${runId}`, token })
  return useQuery(['admin-config-key-sync-runs', runId], queryFn, { enabled: !!runId, ...options })
}

// `configs` is `{ config_key: value }` for one municipality.
export function useUpdateAdminAnalyticConfigValues({ analyticId, options } = {}) {
  const token = useContext(AuthContext)
  return useConfigMutation(
    analyticId,
    ({ municipalityId, configs }) =>
      apiPatch({
        url: `admin/analytics_data/${analyticId}/config_values/${municipalityId}`,
        token,
        objectName: 'configs',
        params: configs
      }),
    options
  )
}
